import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Globe, Mail, MapPin, Pencil, Plus, Tag, Trash2 } from 'lucide-react'
import { BusinessMenuButton, BusinessSidebar } from '@/components/layout/BusinessSidebar'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { BusinessActivityDialog } from '@/components/businesses/BusinessActivityDialog'
import { BusinessAddressDialog } from '@/components/businesses/BusinessAddressDialog'
import { BusinessContactInfoDialog } from '@/components/businesses/BusinessContactInfoDialog'
import { BusinessCountryOperationsDialog } from '@/components/businesses/BusinessCountryOperationsDialog'
import { Empty, Page, PageHeader, Row, Rows, Section } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import type {
  BusinessActivity,
  BusinessAddress,
  BusinessContactInfo,
  BusinessCountryOperation,
} from '@/types'

type BusinessAdditionalInformation = {
  id: string
  name: string
  addresses: BusinessAddress[]
  contact_infos: BusinessContactInfo[]
  country_operations: BusinessCountryOperation[]
  activities: BusinessActivity[]
}

type Props = {
  business: BusinessAdditionalInformation
  countries: { id: string; code: string; name: string }[]
  states: { id: string; code: string; name: string; country_id: string }[]
}

export default function BusinessAdditionalInformation({ business, countries, states }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()

  const [addressDialogOpen, setAddressDialogOpen] = useState(false)
  const [editAddress, setEditAddress] = useState<BusinessAddress | null>(null)
  const [contactInfoDialogOpen, setContactInfoDialogOpen] = useState(false)
  const [editContactInfo, setEditContactInfo] = useState<BusinessContactInfo | null>(null)
  const [countryOpsDialogOpen, setCountryOpsDialogOpen] = useState(false)
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const [editActivity, setEditActivity] = useState<BusinessActivity | null>(null)

  function RowActions({ onEdit, onRemove }: { onEdit?: () => void; onRemove: () => void }) {
    return (
      <>
        {onEdit && (
          <Button variant="ghost" size="icon" className="size-7" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-3.5 text-muted-foreground" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="size-7" onClick={onRemove} aria-label="Remove">
          <Trash2 className="size-3.5 text-muted-foreground" />
        </Button>
      </>
    )
  }

  function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClick}>
        <Plus />
        {label}
      </Button>
    )
  }

  return (
    <DashboardLayout
      title="Additional information"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Additional information' },
      ]}
      guide={<BusinessSidebar businessId={business.id} />}
      mobileGuide={<BusinessMenuButton businessId={business.id} />}
    >
      <Page>
        <PageHeader
          title={<>Additional information</>}
          subtitle={<>Optional information about how to contact your company and where it operates.</>}
        />

        <Section
          title="Addresses"
          action={<AddButton label="Add" onClick={() => { setEditAddress(null); setAddressDialogOpen(true) }} />}
        >
          {business.addresses.length > 0 ? (
            <Rows>
              {business.addresses.map((address) => (
                <Row
                  key={address.id}
                  icon={<MapPin />}
                  title={[address.label, address.street_address_1].filter(Boolean).join(' · ')}
                  description={[
                    address.street_address_2,
                    [address.city, address.state_province].filter(Boolean).join(', '),
                    [address.postal_code, address.country_code].filter(Boolean).join(' · '),
                  ].filter(Boolean).join(', ')}
                  action={
                    <RowActions
                      onEdit={() => { setEditAddress(address); setAddressDialogOpen(true) }}
                      onRemove={async () => {
                        if (await confirm({ description: 'Remove this address?', confirmLabel: 'Remove', destructive: true })) {
                          router.delete(`${basePath}/businesses/${business.id}/addresses/${address.id}`)
                        }
                      }}
                    />
                  }
                />
              ))}
            </Rows>
          ) : (
            <Empty>No address on file yet.</Empty>
          )}
        </Section>

        <Section
          title="Contacts"
          action={<AddButton label="Add" onClick={() => { setEditContactInfo(null); setContactInfoDialogOpen(true) }} />}
        >
          {business.contact_infos.length > 0 ? (
            <Rows>
              {business.contact_infos.map((contact) => (
                <Row
                  key={contact.id}
                  icon={<Mail />}
                  title={[contact.label, contact.email].filter(Boolean).join(' · ')}
                  description={contact.phone || undefined}
                  action={
                    <RowActions
                      onEdit={() => { setEditContactInfo(contact); setContactInfoDialogOpen(true) }}
                      onRemove={async () => {
                        if (await confirm({ description: 'Remove this contact?', confirmLabel: 'Remove', destructive: true })) {
                          router.delete(`${basePath}/businesses/${business.id}/contact_infos/${contact.id}`)
                        }
                      }}
                    />
                  }
                />
              ))}
            </Rows>
          ) : (
            <Empty>No contact on file yet.</Empty>
          )}
        </Section>

        <Section
          title="Where you operate"
          action={
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCountryOpsDialogOpen(true)}>
              <Pencil />
              Edit
            </Button>
          }
        >
          {business.country_operations.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {business.country_operations.map((operation) => (
                <Badge key={operation.id} variant="secondary" className="gap-1.5 font-normal">
                  <Globe className="size-3" />
                  {operation.country_name}
                </Badge>
              ))}
            </div>
          ) : (
            <Empty>Tell us where you do business and we can say what you owe there.</Empty>
          )}
        </Section>

        <Section
          title="What you do"
          action={<AddButton label="Add" onClick={() => { setEditActivity(null); setActivityDialogOpen(true) }} />}
        >
          {business.activities.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {business.activities.map((activity) => (
                <Badge key={activity.id} variant="secondary" className="gap-1.5 pr-1 font-normal">
                  <Tag className="size-3" />
                  {activity.name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${activity.name}`}
                    className="size-5 rounded-full"
                    onClick={async () => {
                      if (await confirm({ description: 'Remove this activity?', confirmLabel: 'Remove', destructive: true })) {
                        router.delete(`${basePath}/businesses/${business.id}/activities/${activity.id}`)
                      }
                    }}
                  >
                    <Trash2 className="size-2.5" />
                  </Button>
                </Badge>
              ))}
            </div>
          ) : (
            <Empty>Nothing listed yet.</Empty>
          )}
        </Section>
      </Page>

      <BusinessAddressDialog
        open={addressDialogOpen}
        onOpenChange={setAddressDialogOpen}
        businessId={business.id}
        address={editAddress}
        basePath={basePath}
        countries={countries}
        states={states}
      />
      <BusinessContactInfoDialog
        open={contactInfoDialogOpen}
        onOpenChange={setContactInfoDialogOpen}
        businessId={business.id}
        contact={editContactInfo}
        basePath={basePath}
      />
      <BusinessCountryOperationsDialog
        open={countryOpsDialogOpen}
        onOpenChange={setCountryOpsDialogOpen}
        businessId={business.id}
        basePath={basePath}
        countries={countries}
        currentOperations={business.country_operations}
      />
      <BusinessActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        businessId={business.id}
        activity={editActivity}
        basePath={basePath}
      />
    </DashboardLayout>
  )
}
